import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Send, Loader2, RefreshCw, Briefcase } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { PageContainer } from "../components/kit";

// Admin side of the candidate conversation. Left: everyone who has written, unread first by recency.
// Right: the thread, with a composer. A reply may carry an application, which the candidate sees as
// a job card with its own "Try again" — we tell them it's fixed, they decide when to re-apply.
const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
};

export default function AdminSupport() {
  const [params, setParams] = useSearchParams();
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(params.get("user") || "");
  const [thread, setThread] = useState(null);
  const [body, setBody] = useState("");
  const [draftId, setDraftId] = useState(params.get("draft") || "");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const loadThreads = useCallback(() => {
    api.get("/admin/support/threads")
      .then(({ data }) => setThreads(data.threads || []))
      .catch(() => toast.error("Could not load conversations"));
  }, []);

  const loadThread = useCallback(async (uid) => {
    if (!uid) return;
    try {
      const { data } = await api.get(`/admin/support/threads/${uid}`);
      setThread(data);
      await api.post(`/admin/support/threads/${uid}/read`);
      loadThreads();
    } catch { toast.error("Could not load this conversation"); }
  }, [loadThreads]);

  useEffect(() => { loadThreads(); }, [loadThreads]);
  useEffect(() => { if (active) loadThread(active); }, [active, loadThread]);
  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [thread?.messages?.length]);

  const open = (uid) => { setActive(uid); params.set("user", uid); setParams(params, { replace: true }); };

  const send = async () => {
    const text = body.trim();
    if (!text || !active) return;
    setSending(true);
    try {
      await api.post(`/admin/support/threads/${active}`, { body: text, draft_id: draftId || undefined });
      setBody(""); setDraftId("");
      await loadThread(active);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send");
    } finally { setSending(false); }
  };

  return (
    <PageContainer width="wide">
      <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-[#0F172A] mb-4">
        <ArrowLeft className="w-4 h-4" /> Admin
      </Link>
      <div className="flex items-center gap-3 mb-4">
        <h1 className="font-display text-2xl font-bold tracking-tight text-[#0F172A]">Candidate messages</h1>
        <button onClick={loadThreads} className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50" title="Refresh">
          <RefreshCw className="w-4 h-4 text-slate-500" />
        </button>
      </div>

      <div className="grid md:grid-cols-[300px_1fr] gap-4 items-start">
        <div className="rounded-xl border border-border bg-white overflow-hidden max-h-[70vh] overflow-y-auto">
          {!threads.length && <div className="p-6 text-sm text-muted-foreground text-center">No conversations yet.</div>}
          {threads.map((t) => (
            <button key={t.user_id} onClick={() => open(t.user_id)}
              className={`w-full text-left px-3.5 py-3 border-b border-border/60 last:border-b-0 hover:bg-slate-50 transition-colors ${
                active === t.user_id ? "bg-[#EAEEFF]" : ""}`}>
              <div className="flex items-center gap-2">
                <span className="text-[13px] font-semibold text-[#0F172A] truncate">{t.name || t.email || t.user_id}</span>
                {t.unread > 0 && (
                  <span className="ml-auto text-[10px] font-extrabold text-white bg-[#E11D48] rounded-full px-1.5 py-0.5 leading-none">{t.unread}</span>
                )}
                <span className={`text-[10px] text-muted-foreground flex-shrink-0 ${t.unread ? "" : "ml-auto"}`}>{timeAgo(t.last_at)}</span>
              </div>
              <div className="text-[11.5px] text-muted-foreground truncate mt-0.5">
                {t.last_sender === "admin" ? "You: " : ""}{t.last_body}
              </div>
            </button>
          ))}
        </div>

        <div className="rounded-xl border border-border bg-white flex flex-col h-[70vh]">
          {!active ? (
            <div className="flex-1 grid place-items-center text-sm text-muted-foreground">Pick a conversation.</div>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-border flex-shrink-0">
                <div className="text-[13px] font-bold text-[#0F172A]">{thread?.name || active}</div>
                <div className="text-[11px] text-muted-foreground">{thread?.email}</div>
              </div>
              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
                {!thread && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>}
                {(thread?.messages || []).map((m) => {
                  const mine = m.sender === "admin";
                  const job = (thread.jobs || {})[m.draft_id];
                  return (
                    <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[80%] rounded-2xl px-3 py-2 ${mine ? "bg-[#0025D8] text-white" : "bg-slate-100 text-[#0F172A]"}`}>
                        <div className="text-[13px] whitespace-pre-wrap leading-relaxed">{m.body}</div>
                        {job && (
                          <div className="mt-2 rounded-lg border border-border bg-white/80 p-2 text-[#0F172A]">
                            <div className="flex items-center gap-1.5 text-[11px]">
                              <Briefcase className="w-3 h-3 flex-shrink-0" />
                              <span className="truncate">{job.company}{job.title ? ` · ${job.title}` : ""}</span>
                              <span className="ml-auto font-mono text-[10px]">{job.status}</span>
                            </div>
                            {job.job_url && (
                              <a href={job.job_url} target="_blank" rel="noreferrer"
                                className="font-mono text-[10px] text-[#0025D8] hover:underline break-all block mt-1">{job.job_url}</a>
                            )}
                          </div>
                        )}
                        <div className={`text-[10px] mt-1 ${mine ? "text-white/60" : "text-muted-foreground"}`}>{timeAgo(m.created_at)}</div>
                      </div>
                    </div>
                  );
                })}
                <div ref={endRef} />
              </div>
              <div className="border-t border-border p-3 flex-shrink-0">
                {draftId && (
                  <div className="flex items-center gap-2 mb-2 text-[11px] text-muted-foreground">
                    <Briefcase className="w-3 h-3" /> attaching <span className="font-mono">{draftId}</span>
                    <span className="text-[10px]">— they'll see it with a Try again button</span>
                    <button onClick={() => setDraftId("")} className="text-[#0025D8] font-semibold hover:underline">remove</button>
                  </div>
                )}
                <div className="flex items-end gap-2">
                  <textarea value={body} onChange={(e) => setBody(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                    rows={3} placeholder="Reply to the candidate…"
                    className="flex-1 resize-none rounded-lg border border-border px-2.5 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#0025D8]/20" />
                  <button onClick={send} disabled={sending || !body.trim()}
                    className="h-9 w-9 grid place-items-center rounded-lg bg-[#0025D8] text-white disabled:opacity-40 flex-shrink-0">
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </PageContainer>
  );
}
